import { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  Building2,
  MapPin,
  Phone,
  User,
  DollarSign,
  Loader2,
  Edit,
  ExternalLink,
  Truck,
  CreditCard,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
} from '@flote/ui';
import { toast } from 'sonner';
import type {
  AdminPartitionFulfillmentStatus,
  AdminPartitionFulfillmentType,
} from '@flote/contracts/http';
import { deliveryTsr } from '../../api';
import { PartitionStatusBadge, PartitionTypeBadge } from '../../components/status-badges';
import { getErrorMessage } from '../../lib/error-utils';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString();
}

const FULFILLMENT_TYPES: { value: AdminPartitionFulfillmentType; label: string }[] = [
  { value: 'flote_driver', label: 'Flote Driver' },
  { value: 'brand_driver', label: 'Brand Driver' },
];

const FULFILLMENT_STATUSES: { value: AdminPartitionFulfillmentStatus; label: string }[] = [
  { value: 'pending_offer', label: 'Pending Offer' },
  { value: 'offer_sent', label: 'Offer Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'expired', label: 'Expired' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function PartitionDetailPage() {
  const { partitionId } = useParams<{ partitionId: string }>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFulfillmentType, setEditFulfillmentType] = useState<AdminPartitionFulfillmentType | ''>('');
  const [editFulfillmentStatus, setEditFulfillmentStatus] = useState<AdminPartitionFulfillmentStatus | ''>('');

  // Fetch partition details
  const {
    data: response,
    isLoading,
    refetch,
  } = deliveryTsr.adminPartitions.getById.useQuery({
    queryKey: ['partition', partitionId],
    queryData: { params: { partitionId: partitionId! } },
  });
  const partition = response?.status === 200 ? response.body : null;

  // Update mutation
  const updateMutation = deliveryTsr.adminPartitions.update.useMutation();

  const handleOpenEditModal = useCallback(() => {
    if (partition) {
      setEditFulfillmentType(partition.fulfillmentType || '');
      setEditFulfillmentStatus(partition.fulfillmentStatus);
    }
    setIsEditModalOpen(true);
  }, [partition]);

  const handleUpdate = useCallback(async () => {
    if (!partitionId) return;

    const updates: {
      fulfillmentType?: AdminPartitionFulfillmentType;
      fulfillmentStatus?: AdminPartitionFulfillmentStatus;
    } = {};

    if (editFulfillmentType && editFulfillmentType !== partition?.fulfillmentType) {
      updates.fulfillmentType = editFulfillmentType;
    }
    if (editFulfillmentStatus && editFulfillmentStatus !== partition?.fulfillmentStatus) {
      updates.fulfillmentStatus = editFulfillmentStatus;
    }

    if (Object.keys(updates).length === 0) {
      toast.info('No changes to save');
      setIsEditModalOpen(false);
      return;
    }

    try {
      const result = await updateMutation.mutateAsync({
        params: { partitionId },
        body: updates,
      });

      if (result.status === 200) {
        toast.success('Partition updated successfully');
        setIsEditModalOpen(false);
        refetch();
      } else {
        const errorBody = result.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to update partition');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update partition'));
    }
  }, [partitionId, partition, editFulfillmentType, editFulfillmentStatus, updateMutation, refetch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!partition) {
    return (
      <div className="space-y-6">
        <Link to="/partitions" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Partitions
        </Link>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="mb-2 text-lg font-medium">Partition not found</h3>
            <p className="text-sm text-muted-foreground">
              The partition you're looking for doesn't exist or has been removed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canEdit = !['completed', 'cancelled'].includes(partition.fulfillmentStatus);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link to="/partitions" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Partitions
        </Link>
        <div className="flex gap-2">
          {canEdit && (
            <Button variant="outline" onClick={handleOpenEditModal}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
          )}
          {partition.onfleetTaskId && (
            <Button variant="outline" asChild>
              <a
                href={`https://onfleet.com/dashboard#/tasks/${partition.onfleetTaskId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                View in Onfleet
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Partition Info Card */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-4">
              <h1 className="font-mono text-2xl font-bold">{partition.trackingId}</h1>
              <PartitionTypeBadge type={partition.fulfillmentType} />
              <PartitionStatusBadge status={partition.fulfillmentStatus} />
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {/* Brand */}
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Brand</p>
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span>{partition.brandName || 'Unknown'}</span>
                </div>
              </div>

              {/* Total */}
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Portion Total</p>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span className="text-lg font-semibold">{formatCurrency(partition.portionTotal)}</span>
                </div>
                {partition.portionSubtotal !== partition.portionTotal && (
                  <p className="text-xs text-muted-foreground">
                    Subtotal: {formatCurrency(partition.portionSubtotal)}
                  </p>
                )}
              </div>

              {/* Payment Method */}
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Payment Method</p>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <span>{partition.paymentMethod || 'Not specified'}</span>
                </div>
              </div>

              {/* Stock Location */}
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Stock Location</p>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{partition.stockLocationName || 'Not assigned'}</span>
                </div>
              </div>
            </div>

            {/* Spread Info */}
            {(partition.spreadRate || partition.spreadAmount) && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-muted-foreground mb-2">Delivery Fee</p>
                <div className="flex gap-6">
                  {partition.spreadRate && (
                    <div>
                      <span className="text-sm text-muted-foreground">Rate: </span>
                      <span className="font-medium">{partition.spreadRate}%</span>
                    </div>
                  )}
                  {partition.spreadAmount && (
                    <div>
                      <span className="text-sm text-muted-foreground">Amount: </span>
                      <span className="font-medium">{formatCurrency(partition.spreadAmount)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="border-t pt-4 text-sm text-muted-foreground">
              <p>Created: {formatDateTime(partition.createdAt)}</p>
              <p>Updated: {formatDateTime(partition.updatedAt)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customer Info Card */}
      {(partition.customerName || partition.customerPhone || partition.customerAddress) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Customer Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-3">
              {partition.customerName && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Name</p>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{partition.customerName}</span>
                  </div>
                </div>
              )}
              {partition.customerPhone && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Phone</p>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{partition.customerPhone}</span>
                  </div>
                </div>
              )}
              {partition.customerAddress && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Address</p>
                  <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <span>{partition.customerAddress}</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Partition Items Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Items ({partition.items?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!partition.items || partition.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Package className="mb-2 h-8 w-8 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No items in this partition</p>
            </div>
          ) : (
            <div className="-mx-4 overflow-x-auto sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="hidden sm:table-cell">SKU</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="hidden md:table-cell text-right">Reserved</TableHead>
                      <TableHead className="hidden md:table-cell text-right">Fulfilled</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partition.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <span className="font-medium">{item.productName || 'Unknown Product'}</span>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell font-mono text-sm text-muted-foreground">
                          {item.productCode || '—'}
                        </TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="hidden md:table-cell text-right text-muted-foreground">
                          {item.reservedQuantity}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-right text-muted-foreground">
                          {item.fulfilledQuantity}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(item.lineTotal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* External Links Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Related Resources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {partition.onfleetTaskId && (
              <Button variant="outline" asChild>
                <a
                  href={`https://onfleet.com/dashboard#/tasks/${partition.onfleetTaskId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Onfleet Task
                </a>
              </Button>
            )}
            {partition.b2bwaveOrderId && (
              <Button variant="outline" asChild>
                <a
                  href={`https://app.b2bwave.com/orders/${partition.b2bwaveOrderId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  B2BWave Order
                </a>
              </Button>
            )}
            {!partition.onfleetTaskId && !partition.b2bwaveOrderId && (
              <p className="text-sm text-muted-foreground">No external resources linked</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Partition</DialogTitle>
            <DialogDescription>
              Update the fulfillment type and status for this partition. This will affect how the order is delivered.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="fulfillmentType">Fulfillment Type</Label>
              <Select
                value={editFulfillmentType}
                onValueChange={(v) => setEditFulfillmentType(v as AdminPartitionFulfillmentType)}
              >
                <SelectTrigger id="fulfillmentType">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {FULFILLMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fulfillmentStatus">Fulfillment Status</Label>
              <Select
                value={editFulfillmentStatus}
                onValueChange={(v) => setEditFulfillmentStatus(v as AdminPartitionFulfillmentStatus)}
              >
                <SelectTrigger id="fulfillmentStatus">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {FULFILLMENT_STATUSES.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
